import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Globe, Send, User, MessageSquare } from 'lucide-react';
import styles from './Contact.module.css';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';

const Contact = () => {
  const requiredFields = ['name', 'email', 'phone', 'country', 'message'];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [validationMessage, setValidationMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setFieldErrors(prev => ({ ...prev, [name]: false }));
    if (validationMessage) {
      setValidationMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    requiredFields.forEach((field) => {
      if (!formData[field]?.trim()) {
        nextErrors[field] = true;
      }
    });

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setValidationMessage('Please fill all required fields before sending enquiry.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');
    setValidationMessage('');

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';

    try {
      await axios.post(`${apiUrl}/api/enquiry`, {
        fullName: formData.name,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        message: formData.message
      });

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        country: '',
        message: ''
      });

      setTimeout(() => setSubmitStatus(''), 5000);
    } catch (error) {
      console.error('Enquiry submit failed:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactDetails = [
    {
      icon: <Phone size={22} />,
      label: 'Direct Communication',
      lines: [
        { text: '+91 9557005492', href: 'tel:+919557005492' },
        { text: '+91 9759599922', href: 'tel:+919759599922' },
      ],
      subValue: 'Sales & Logistics Support',
      theme: 'orange',
    },
    {
      icon: <Mail size={22} />,
      label: 'Institutional Email',
      lines: [{ text: 'info@greeneripapers.com', href: 'mailto:info@greeneripapers.com' }],
      subValue: '24-Hour Response Guarantee',
      theme: 'dark',
    },
    {
      icon: <MapPin size={22} />,
      label: 'Production Facility',
      lines: [
        { text: 'Khasra no-187, Village Kanshi' },
        { text: 'Gagol Road, Partapur, Meerut - 250103' },
      ],
      subValue: 'Industrial Hub Unit - I',
      theme: 'light',
    },
  ];

  return (
    <>
      <Navigation />
      <section className={styles.wrapper}>
        <div className={styles.container}>
        
        {/* TOP CENTER HEADER */}
        <motion.div 
          className={styles.headerSection}
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h1 className={styles.mainTitle}>
            Powering Industries with <span className={styles.orange}>Reliable Paper</span> Solutions.
          </h1>
          <p className={styles.mainDesc}>
            Based in the industrial heart of Meerut, our state-of-the-art manufacturing unit at 
            <strong> Partapur</strong> specializes in high-precision paper engineering. 
            From sustainable food packaging to specialized industrial rolls, we combine advanced 
            technology with eco-friendly practices to serve clients across the globe.
          </p>
        </motion.div>

        {/* EQUAL ALIGNED CONTENT GRID */}
        <div className={styles.contentGrid}>
          
          {/* LEFT COLUMN: Contact Information */}
          <motion.div
            className={styles.infoColumn}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className={styles.infoPanel}>
              <span className={styles.infoCutBar} aria-hidden="true" />
              <span className={styles.infoCutCorner} aria-hidden="true" />

              <header className={styles.infoPanelHead}>
                <span className={styles.infoKicker}>Contact details</span>
                <h2 className={styles.infoTitle}>Get in touch</h2>
                <p className={styles.infoLead}>
                  Reach our sales team for bulk orders, specs, and dispatch timelines.
                </p>
              </header>

              <ul className={styles.infoList}>
                {contactDetails.map((item) => {
                  const rowTheme =
                    item.theme === 'orange'
                      ? styles.rowOrange
                      : item.theme === 'dark'
                        ? styles.rowDark
                        : styles.rowLight;

                  return (
                    <li key={item.label} className={styles.infoListItem}>
                      <div className={`${styles.infoRow} ${rowTheme}`}>
                        <span className={styles.infoIcon} aria-hidden="true">
                          {item.icon}
                        </span>
                        <div className={styles.infoBody}>
                          <span className={styles.infoLabel}>{item.label}</span>
                          {item.lines.map((line) =>
                            line.href ? (
                              <a
                                key={line.text}
                                href={line.href}
                                className={styles.infoValueLink}
                              >
                                {line.text}
                              </a>
                            ) : (
                              <p key={line.text} className={styles.infoValue}>
                                {line.text}
                              </p>
                            )
                          )}
                          <p className={styles.infoNote}>{item.subValue}</p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </motion.div>

          {/* RIGHT COLUMN: Send Enquiry Form */}
          <motion.div 
            className={styles.formColumn}
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className={styles.formWrapper}>
              <div className={styles.formHeader}>
                <div className={styles.formIcon}>
                  <Send size={24} />
                </div>
                <div>
                  <h3 className={styles.formTitle}>Send Enquiry</h3>
                  <p className={styles.formSubtitle}>Get in touch with our sales team for bulk orders and custom solutions</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className={styles.enquiryForm}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      <User size={16} />
                      Full Name <span className={styles.requiredMark}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`${styles.formInput} ${fieldErrors.name ? styles.inputError : ''}`}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      <Mail size={16} />
                      Email Address <span className={styles.requiredMark}>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`${styles.formInput} ${fieldErrors.email ? styles.inputError : ''}`}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      <Phone size={16} />
                      Phone Number <span className={styles.requiredMark}>*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`${styles.formInput} ${fieldErrors.phone ? styles.inputError : ''}`}
                      placeholder="+91 98765 43210"
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      <Globe size={16} />
                      Country <span className={styles.requiredMark}>*</span>
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className={`${styles.formInput} ${fieldErrors.country ? styles.inputError : ''}`}
                      placeholder="Your country"
                      required
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <MessageSquare size={16} />
                    Message <span className={styles.requiredMark}>*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`${styles.formTextarea} ${fieldErrors.message ? styles.inputError : ''}`}
                    placeholder="Tell us about your requirements, quantity needed, delivery timeline, etc."
                    rows="4"
                    required
                  ></textarea>
                </div>

                {validationMessage && (
                  <p className={styles.validationMessage}>{validationMessage}</p>
                )}

                {submitStatus && (
                  <div className={`${styles.formMessage} ${styles[submitStatus]}`}>
                    {submitStatus === 'success' ? (
                      <>
                        <div className={styles.successIcon}>✓</div>
                        <span>Thank you! Your enquiry has been sent successfully. We'll contact you within 24 hours.</span>
                      </>
                    ) : (
                      <>
                        <div className={styles.errorIcon}>✕</div>
                        <span>Sorry! There was an error sending your enquiry. Please try again or call us directly.</span>
                      </>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={styles.submitButton}
                >
                  {isSubmitting ? (
                    <>
                      <div className={styles.spinner}></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Enquiry
                    </>
                  )}
                </button>
              </form>
            </div>
          </motion.div>

        </div>

        {/* MAP SECTION BELOW */}
        <motion.div 
          className={styles.mapSection}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className={styles.mapWrapper}>
            <div className={styles.mapStatus}>
              <div className={styles.pulse}></div>
              <span>Meerut Manufacturing Plant</span>
            </div>
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3493.5786196236955!2d77.6534575!3d28.8812608!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDUyJzUyLjUiTiA3N8KwMzknMTIuNCJF!5e0!3m2!1sen!2sin!4v1715100000000" 
              className={styles.mapIframe}
              allowFullScreen="" 
              loading="lazy"
            ></iframe>
            <div className={styles.addressBar}>
              <MapPin size={20} color="#ff671b" />
              <p>Khasra no-187, Village Kanshi, Gagol Road, Partapur, Meerut - 250103</p>
            </div>
          </div>
        </motion.div>
      </div>
      </section>
      <Footer />
    </>
  );
};

export default Contact;